// Account Panel Component with Subscription Info
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useNotes } from '../../contexts/NotesContext.jsx';
import { getPlanDisplayName, isExpired, getDaysRemaining } from '../../services/pro-status.js';
import './Settings.css';

export default function Account() {
    const { user, logout, proStatus, proLoading: loading, refreshProStatus } = useAuth();
    const { allNotes } = useNotes();

    const handleLogout = async () => {
        if (window.confirm('Are you sure you want to sign out?')) {
            try {
                await logout();
            } catch (err) {
                console.error('Logout failed:', err);
            }
        }
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getExpirationStatus = () => {
        if (!proStatus?.expiresAt) {
            return proStatus?.isPro ? 'Never expires' : null;
        }

        const expired = isExpired(proStatus);
        const daysLeft = getDaysRemaining(proStatus.expiresAt);

        if (expired) {
            return <span className="status-expired">Expired</span>;
        }

        if (daysLeft <= 7) {
            return <span className="status-warning">Expires in {daysLeft} days</span>;
        }

        return <span className="status-active">{daysLeft} days remaining</span>;
    };

    return (
        <div className="account-panel">
            <h2 className="panel-title">Account</h2>

            <div className="account-card">
                {user && (
                    <>
                        <div className="user-profile">
                            <img
                                src={user.photoURL || 'https://www.gravatar.com/avatar/?d=mp'}
                                alt={user.displayName}
                                className="profile-photo"
                            />
                            <div className="profile-name">{user.displayName}</div>
                            <div className="profile-email">{user.email}</div>
                        </div>

                        {/* Subscription Section */}
                        <div className="settings-section">
                            <div className="settings-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>Subscription</span>
                                <button
                                    className="btn btn-secondary"
                                    style={{ fontSize: '11px', padding: '2px 8px' }}
                                    onClick={refreshProStatus}
                                    disabled={loading}
                                >
                                    {loading ? '...' : 'Refresh'}
                                </button>
                            </div>

                            {loading ? (
                                <div className="setting-item">
                                    <div className="setting-desc">Loading subscription info...</div>
                                </div>
                            ) : (
                                <>
                                    <div className="setting-item">
                                        <div>
                                            <div className="setting-label">Plan</div>
                                            <div className="setting-desc">
                                                <span className={`plan-badge ${proStatus?.isPro ? 'live' : 'free'}`}>
                                                    {getPlanDisplayName(proStatus?.proType)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {proStatus?.isPro && (
                                        <>
                                            <div className="setting-item">
                                                <div>
                                                    <div className="setting-label">Status</div>
                                                    <div className="setting-desc">
                                                        {getExpirationStatus()}
                                                    </div>
                                                </div>
                                            </div>

                                            {proStatus.expiresAt && (
                                                <div className="setting-item">
                                                    <div>
                                                        <div className="setting-label">Expires On</div>
                                                        <div className="setting-desc">
                                                            {formatDate(proStatus.expiresAt)}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {proStatus.activatedAt && (
                                                <div className="setting-item">
                                                    <div>
                                                        <div className="setting-label">Activated On</div>
                                                        <div className="setting-desc">
                                                            {formatDate(proStatus.activatedAt)}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {proStatus.promoCodeUsed && (
                                                <div className="setting-item">
                                                    <div>
                                                        <div className="setting-label">Promo Code</div>
                                                        <div className="setting-desc" style={{ fontFamily: 'monospace' }}>
                                                            {proStatus.promoCodeUsed}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {!proStatus?.isPro && (
                                        <div className="setting-item">
                                            <div className="setting-desc" style={{ color: 'var(--text-muted)' }}>
                                                Upgrade to Live in the Chrome extension to unlock premium features
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Storage Section */}
                        <div className="settings-section">
                            <div className="settings-title">Storage</div>
                            <div className="setting-item">
                                <div>
                                    <div className="setting-label">Total Notes</div>
                                    <div className="setting-desc">{allNotes.length} notes synced</div>
                                </div>
                            </div>
                            <div className="setting-item">
                                <div>
                                    <div className="setting-label">User ID</div>
                                    <div className="setting-desc" style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                                        {user.uid}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="settings-section" style={{ borderBottom: 'none', textAlign: 'center' }}>
                            <button className="btn btn-danger" onClick={handleLogout}>
                                Sign Out
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
